import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import HoverTooltip from "../../components/common/HoverTooltip";
import { Link } from "react-router-dom";
import { ListChecks, Users, ShieldCheck, Info } from "lucide-react";
import AssignmentRules from "../../components/configurations/AssignmentRules";
import { CompletionRules } from "../../components/configurations/CompletionRules";
import AppContext from "../../context/AppContext";
import translations from "../../components/common/Translations";
import AssignmentTable from "../../components/configurations/AssignmentRule/AssignmentTable";
import ActivitySelect from "../../components/common/task/ActivitySelect";
import { getActivityListByProjectId } from "../../service/User";
import { getPermittedActivities } from "../../utils/activityPermissions";
import ReactSelector from "../../components/common/CommonReactSelector";
import { components } from "react-select";
import { hasScreenAccess } from "../../utils/permissions";

const Placeholder = (props) => {
  return <components.Placeholder {...props} />;
};

// "What this screen does" popover content per tab -- same idea as the
// prototype's TAB_INFO map, keyed by our real tab ids.
const CONFIG_TAB_INFO = {
  completion: { labelKey: "complitionrule", infoKey: "completionRuleInfo", tooltipKey: "completionRuleTabTooltip" },
  assignment: { labelKey: "assignment", infoKey: "assignmentRuleInfo", tooltipKey: "assignmentRuleTabTooltip" },
  "special-assignment": { labelKey: "splaignrule", infoKey: "specialAssignmentRuleInfo", tooltipKey: "specialAssignmentRuleTabTooltip" },
};

const ConfigTabInfoPopover = ({ activeTab, langMode }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const info = CONFIG_TAB_INFO[activeTab];

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  if (!info) return null;

  return (
    <div
      className="relative shrink-0"
      ref={ref}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={translations[langMode].whatThisScreenDoes}
        className="h-9 w-9 rounded-full bg-[#F2F6FA] text-[#236DB4] hover:bg-[#E0EBF4] flex items-center justify-center transition-colors"
      >
        <Info size={16} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-30 w-72 sm:w-80 rounded-xl border border-gray-200 bg-white shadow-xl p-4" style={{ maxWidth: "85vw" }}>
          <p className="text-sm font-semibold text-gray-800 mb-1">
            {translations[langMode].whatThisScreenDoes}
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            {translations[langMode][info.infoKey]}
          </p>
        </div>
      )}
    </div>
  );
};

// Hovering a tab shows the shared blue tooltip. Reuses each tab's existing
// CONFIG_TAB_INFO description, so no new translation keys are needed.
const ConfigTabButton = ({ active, number, label, Icon, tooltipText, onClick }) => {
  return (
    <div className="col-span-1 sm:col-auto relative inline-flex min-w-0 w-full sm:w-auto">
      <HoverTooltip text={tooltipText} position="top" wrapperClassName="flex min-w-0 w-full sm:w-auto">
      <button
        type="button"
        onClick={onClick}
        className={`flex w-full sm:w-auto items-center justify-between sm:justify-start gap-1 sm:gap-2 px-2 py-2 sm:px-3.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-150 min-w-0 ${
          active
            ? "bg-[#236DB4] text-white shadow-sm shadow-[#236DB4]/25"
            : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
        }`}
      >
        <span className="flex items-center gap-1.5 min-w-0 truncate">
          <span
            className={`flex items-center justify-center h-4 w-4 sm:h-5 sm:w-5 rounded-full text-[10px] sm:text-[11px] font-bold shrink-0 transition-colors ${
              active ? "bg-white/25 text-white" : "bg-slate-100 text-slate-500"
            }`}
          >
            {number}
          </span>
          <span className="truncate">{label}</span>
        </span>
        <Icon size={14} className={`shrink-0 ml-1 ${active ? "text-white" : "text-slate-400"}`} />
      </button>
      </HoverTooltip>
    </div>
  );
};

const getDefaultConfigurationTab = () => {
  if (hasScreenAccess("CONFIGURATION", "canView")) return "completion";
  if (hasScreenAccess("ASSIGNMENT_RULES", "canView")) return "assignment";
  if (hasScreenAccess("SPECIAL_ASSIGNMENT_RULES", "canView")) return "special-assignment";
  return "completion";
};

const ConfigurationMain = () => {
  const [activeTab, setActiveTab] = useState(getDefaultConfigurationTab);
  const hasSpecialAssignmentRulesAccess = hasScreenAccess("SPECIAL_ASSIGNMENT_RULES", "canView");
  const hasAssignmentRulesAccess = hasScreenAccess("ASSIGNMENT_RULES", "canView");
  const hasConfigurationRulesAccess = hasScreenAccess("CONFIGURATION", "canView");
  
  const {
    theme,
    langMode,
    setWorkflowName,
    workflowName,
    selectedOrgId,
    updateActivityId,
    projectId,
    activityLoader,
    activityId,
    setWorkflowId,
    setHeaderActivitySelect,
  } = useContext(AppContext);

  const [activity, setActivity] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(0);

  // Search state for all 3 Configuration tabs, lifted up here since this
  // component stays mounted across tab switches while each tab's own
  // component (CompletionRules/AssignmentRules/AssignmentTable)
  // unmounts on tab-away (see the conditional render below) -- without this,
  // each tab's local useState reset the typed term/field on every switch.
  // Clearing manually (the X button) or changing the search field still
  // resets the value the same way each component already did, just through
  // these lifted setters now.
  const [completionSearch, setCompletionSearch] = useState("");
  const [completionSearchScope, setCompletionSearchScope] = useState("status");
  const [assignmentSearch, setAssignmentSearch] = useState("");
  const [assignmentSearchScope, setAssignmentSearchScope] = useState("assignTo");
  const [specialAssignmentSearch, setSpecialAssignmentSearch] = useState("");
  const [specialAssignmentSearchScope, setSpecialAssignmentSearchScope] = useState("assignTo");

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
  };

  useEffect(() => {
    const tabAccess = {
      completion: hasConfigurationRulesAccess,
      assignment: hasAssignmentRulesAccess,
      "special-assignment": hasSpecialAssignmentRulesAccess,
    };

    if (!tabAccess[activeTab]) {
      setActiveTab(getDefaultConfigurationTab());
    }
  }, [
    activeTab,
    hasConfigurationRulesAccess,
    hasAssignmentRulesAccess,
    hasSpecialAssignmentRulesAccess,
  ]);

  useEffect(() => {
    const fetchActivityList = async () => {
      try {
        const data = await getActivityListByProjectId(projectId);
        if (Array.isArray(data)) {
          setActivity(data);
        } else {
          console.warn("getActivityListByProjectId did not return an array:", data);
          setActivity([]);
        }
      } catch (error) {
        console.error("Error fetching activities:", error);
        setActivity([]);
      }
    };

    fetchActivityList();
  }, [projectId]);

  const handleChangeActivity = async (selectedValue) => {
    updateActivityId(selectedValue.value);
    setSelectedActivity(selectedValue);
    localStorage.setItem("workflowName", selectedValue.label);

  };

  const permittedActivity = useMemo(() => {
    return getPermittedActivities(activity);
  }, [activity]);

  const option = useMemo(() => {
    return (permittedActivity || [])
      .filter(Boolean)
      .map((item) => ({
        value: item.workflowId,
        label: item.workflowName,
      }));
  }, [permittedActivity]);

  const defaultActivityValue = useMemo(() => {
    if (projectId !== null && permittedActivity?.length) {
      if (activityId) {
        const activityIdList =
          typeof activityId === "string"
            ? activityId.split(",")
            : Array.isArray(activityId)
              ? activityId.map((id) => id.toString())
              : [activityId?.toString()];

        const matched = permittedActivity
          .filter((org) => activityIdList.includes(org.workflowId?.toString()))
          .map((dept) => ({
            value: dept.workflowId,
            label: dept.workflowName,
          }));

        if (matched.length > 0) return matched;
      }

      return [{
        value: permittedActivity[0].workflowId,
        label: permittedActivity[0].workflowName,
      }];
    }
    return [];
  }, [projectId, activityId, permittedActivity]);

  const dynamicHeight = useMemo(() => {
    const count = Array.isArray(selectedActivity) ? selectedActivity.length : 0;
    return count > 0 ? count * 27 + "px" : "42px";
  }, [selectedActivity]);

  useEffect(() => {
    if (defaultActivityValue && defaultActivityValue.length > 0) {
      const valueString = defaultActivityValue.map((item) => item.value).join(",");
      setWorkflowId(valueString);
      updateActivityId(valueString);
      localStorage.setItem("activityId", valueString);

      setSelectedActivity(defaultActivityValue[0]);
    }
  }, [defaultActivityValue]);

  // Render the Activity select into the top header bar (next to the
  // Department/DCRC selects) instead of this page's own body, for the two
  // tabs that need it -- same condition this dropdown was already hidden
  // under below. Cleared on tab switch/unmount so it doesn't leak into
  // Assignment Rules / Special Assignment Rule or other pages.
  useEffect(() => {
    if (!setHeaderActivitySelect) return undefined;

    const showActivitySelect =
      !activityLoader && activeTab === "completion";

    if (!showActivitySelect) {
      setHeaderActivitySelect(null);
      return undefined;
    }

    setHeaderActivitySelect(
      <ReactSelector
        options={projectId !== null ? option : []}
        onChange={handleChangeActivity}
        components={Placeholder}
        placeholder={
          projectId !== null || activityId
            ? option.length > 0
              ? translations[langMode].select
              : translations[langMode].noactivity
            : translations[langMode].noactivity
        }
        isClearable={false}
        height={dynamicHeight || "42px"}
        menuPortalTarget={document.body}
        value={selectedActivity}
      />
    );

    return () => setHeaderActivitySelect(null);
  }, [
    setHeaderActivitySelect,
    activeTab,
    activityLoader,
    option,
    selectedActivity,
    dynamicHeight,
    projectId,
    activityId,
    langMode,
  ]);

  const configTabs = [
    { id: "completion", labelKey: "complitionrule", Icon: ListChecks, visible: hasConfigurationRulesAccess },
    { id: "assignment", labelKey: "assignment", Icon: Users, visible: hasAssignmentRulesAccess },
    { id: "special-assignment", labelKey: "splaignrule", Icon: ShieldCheck, visible: hasSpecialAssignmentRulesAccess },
  ].filter((t) => t.visible);

  const tabBar = (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 w-full min-w-0">
      <div className="flex sm:hidden items-center justify-between gap-2 w-full">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
          {translations[langMode].configuration ?? "Configuration"}
        </span>
        <ConfigTabInfoPopover activeTab={activeTab} langMode={langMode} />
      </div>

      <div
        className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-1.5 sm:gap-1 rounded-xl border border-slate-200 bg-white dark:bg-gray-800 dark:border-gray-700 p-1 sm:p-1.5 shadow-sm w-full sm:w-auto min-w-0"
        role="group"
      >
        {configTabs.map(({ id, labelKey, Icon }, i) => {
          const active = activeTab === id;
          return (
            <ConfigTabButton
              key={id}
              active={active}
              number={i + 1}
              label={translations[langMode][labelKey]}
              Icon={Icon}
              tooltipText={translations[langMode][CONFIG_TAB_INFO[id]?.tooltipKey]}
              onClick={() => handleTabClick(id)}
            />
          );
        })}
      </div>

      <div className="hidden sm:block shrink-0">
        <ConfigTabInfoPopover activeTab={activeTab} langMode={langMode} />
      </div>
    </div>
  );

  return (
    <div
      className={`${theme === "dark"
          ? "dark-mode"
          : theme === "high-contrast"
            ? "high-contrast"
            : ""
        } flex flex-col w-full min-h-full sm:h-full sm:min-h-0 overflow-y-auto sm:overflow-hidden p-2.5 sm:p-4`}
    >
      <div className="flex flex-col w-full h-full min-h-0 overflow-hidden">
        <div
          className={`w-full h-full flex-1 min-h-0 flex flex-col rounded-md overflow-hidden ${theme === "dark"
              ? " text-white "
              : theme === "high-contrast"
                ? "high-contrast"
                : "text-gray-700 "
            }`}
        >
          {activeTab === "completion" && hasConfigurationRulesAccess && (
            <CompletionRules
              tabBar={tabBar}
              search={completionSearch}
              onSearchChange={setCompletionSearch}
              searchScope={completionSearchScope}
              onSearchScopeChange={setCompletionSearchScope}
            />
          )}
          {activeTab === "assignment" && hasAssignmentRulesAccess && (
            <AssignmentRules
              tabBar={tabBar}
              search={assignmentSearch}
              onSearchChange={setAssignmentSearch}
              searchScope={assignmentSearchScope}
              onSearchScopeChange={setAssignmentSearchScope}
            />
          )}
          {activeTab === "special-assignment" && hasSpecialAssignmentRulesAccess && (
            <AssignmentTable
              tabBar={tabBar}
              search={specialAssignmentSearch}
              onSearchChange={setSpecialAssignmentSearch}
              searchScope={specialAssignmentSearchScope}
              onSearchScopeChange={setSpecialAssignmentSearchScope}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfigurationMain;
